import 'bootstrap/dist/css/bootstrap.css';
import { useState } from 'react';
import Spinner from '../../../shared/spinner';
import { format, parseISO } from 'date-fns';
import { OperacaoParcela } from '../../../shared/model/operacaoParcela';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faClose, faCalculator, faSearch } from '@fortawesome/free-solid-svg-icons';
import InputMask from 'react-input-mask';
import OperacaoParcelaService from '../../../services/operacaoParcelaService';
import CustomAlert from '../../../shared/customAlert';
import { BaixaParcelaDTO } from '../../../shared/dto/baixaParcelaDTO';

const formatarValorMoeda = (valor: number | null | undefined): string => {
  return (valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function RepactuacaoParcelaComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [exibeDetalhe, setExibeDetalhe] = useState(false);

  const [contrato, setContrato] = useState('');
  const [nomeCliente, setNomeCliente] = useState('');
  const [faixaParcela1, setFaixaParcela1] = useState('');
  const [faixaParcela2, setFaixaParcela2] = useState('');
  const [todasParcelas, setTodasParcelas] = useState<OperacaoParcela[]>([]);
  const [parcelasList, setParcelasList] = useState<OperacaoParcela[]>([]);
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<number[]>([]);

  const [baixaDto, setBaixaDto] = useState<BaixaParcelaDTO>();
  const [valorPago, setValorPago] = useState<number>(0);
  const [valorPagoInput, setValorPagoInput] = useState<number>(0);

  const operacaoParcelaService: OperacaoParcelaService = new OperacaoParcelaService();

  const listarContrato2 = async () => {
    if (contrato === '') return setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
    try {
      setLoading(true);
      limpaDados();

      const responseList = await operacaoParcelaService.listarParcelasPorContrato(contrato);
      const parcelas: OperacaoParcela[] = responseList.data;

      if (!parcelas || parcelas.length === 0) {
        setAlert({ message: 'Nenhuma parcela encontrada.', type: 'warning' });
        setLoading(false);
        return;
      }

      setTodasParcelas(parcelas);
      setNomeCliente("Cliente do Contrato " + contrato);
      
      const parcelasAbertas = parcelas.filter(p => !p.dtMovimentacao && (!p.tipoMovimentacao || p.tipoMovimentacao.trim() === ''));
      if (parcelasAbertas.length > 0) {
        setFaixaParcela1(parcelasAbertas[0].numeroParcela?.toString() || '');
        setFaixaParcela2(parcelasAbertas[parcelasAbertas.length - 1].numeroParcela?.toString() || '');
      }

      setExibeDetalhe(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setAlert({ message: 'Não há parcelas em aberto para este Contrato.', type: 'warning' });
      console.error(err);
    }
  };

  const listarContrato = async () => {
    if (contrato === '') return setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
    try {
      setParcelasList([]);
      setLoading(true);

      const responseList = await operacaoParcelaService.listarParcelasParaBaixa(contrato, new BaixaParcelaDTO());
      const json = responseList.data;

      setTodasParcelas(json.parcelas);
      setBaixaDto(json);
      // setParcelasList(null);
      setParcelasSelecionadas([]);
      //setParcelasList(json.parcelas);
      setNomeCliente(json.nomeCliente);
      setFaixaParcela1(json.faixaParcela1.toString());
      setFaixaParcela2(json.faixaParcela2.toString());
      setValorPago(json.saldoTotal);
      setValorPagoInput(json.saldoTotal);

      if (json.bloqueioPropostaEsteira !== null) {
        setAlert({ message: 'Não é possível baixar parcelas deste contrato, proposta - ' + json.bloqueioPropostaEsteira + ' - de REFIN na esteira.', type: 'warning' });
      }

      setExibeDetalhe(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setAlert({ message: 'Não há saldo em aberto para este Contrato ou Contrato foi liquidado!', type: 'warning' });
      console.error(err);
    }
  }

const calcularFaixa = () => {
    if (contrato === '') return setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
    if (!faixaParcela1 || !faixaParcela2) return setAlert({ message: 'Informe a faixa de parcelas.', type: 'warning' });

    const inicio = parseInt(faixaParcela1);
    const fim = parseInt(faixaParcela2);

    if (inicio > fim) {
      return setAlert({ message: 'A parcela inicial não pode ser maior que a parcela final.', type: 'warning' });
    }

    const parcelasFiltradas = todasParcelas.filter(
      p => p.numeroParcela! >= inicio && p.numeroParcela! <= fim
    );

    if (parcelasFiltradas.length === 0) {
      setParcelasList([]);
      setParcelasSelecionadas([]);
      return setAlert({ message: 'Nenhuma parcela encontrada nesta faixa.', type: 'warning' });
    }

    setParcelasList(parcelasFiltradas);

    const selecionadasNaFaixa = parcelasFiltradas
      .filter(p => {
        const tipoMov = (p.tipoMovimentacao || '').toUpperCase();
        const bloqueada = Boolean(p.dtMovimentacao) || (p.valorPago != null && p.valorPago > 0) || tipoMov.includes('REPACTU') || tipoMov.includes('BAIXA');
        return !bloqueada;
      })
      .map(p => p.numeroParcela!);

    setParcelasSelecionadas(selecionadasNaFaixa);
  };

  const toggleSelecionarParcela = (numParcela: number) => {
    setParcelasSelecionadas(prev => 
      prev.includes(numParcela) ? prev.filter(n => n !== numParcela) : [...prev, numParcela]
    );
  };

  const executarRepactuacaoLote = async () => {
    if (parcelasSelecionadas.length === 0) {
      return setAlert({ message: 'Selecione ao menos uma parcela válida para repactuar.', type: 'warning' });
    }

    try {
      setLoading(true);
      const requestPayload = {
        numeroContrato: contrato,
        faixaInicio: Math.min(...parcelasSelecionadas),
        faixaFim: Math.max(...parcelasSelecionadas)
      };

      await operacaoParcelaService.repactuarParcelasPorFaixa(requestPayload);
      
      setAlert({ message: `Faixa de parcelas repactuada com sucesso.`, type: 'success' });
      setParcelasSelecionadas([]);
      setParcelasList([]);
      
      await listarContrato();
    } catch (err: any) {
      setLoading(false);
      const mensagemErro = err.response?.data || 'Ocorreu um erro ao repactuar as parcelas.';
      setAlert({ message: mensagemErro, type: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const limpaDados = () => {
    setExibeDetalhe(false);
    setNomeCliente('');
    setFaixaParcela1('');
    setFaixaParcela2('');
    setTodasParcelas([]);
    setParcelasList([]);
    setParcelasSelecionadas([]);
  };

  const parcelasElegiveisNaTela = parcelasList!.filter(item => {
    const tipoMov = (item.tipoMovimentacao || '').toUpperCase();
    const jaFoiMovimentada = Boolean(item.dtMovimentacao) || (item.valorPago != null && item.valorPago > 0);
    const jaFoiRepactuadaOuBaixada = tipoMov.includes('REPACTU') || tipoMov.includes('BAIXA') || tipoMov.includes('ESTORNO');
    return !(jaFoiMovimentada || jaFoiRepactuadaOuBaixada);
  }).map(p => p.numeroParcela!);

  const todasParcelasSelecionadas = parcelasElegiveisNaTela.length > 0 && 
    parcelasElegiveisNaTela.every(num => parcelasSelecionadas.includes(num));

  const toggleSelecionarTodas = (marcar: boolean) => {
    if (marcar) {
      const novasSelecionadas = Array.from(new Set([...parcelasSelecionadas, ...parcelasElegiveisNaTela]));
      setParcelasSelecionadas(novasSelecionadas);
    } else {
      const novasSelecionadas = parcelasSelecionadas.filter(num => !parcelasElegiveisNaTela.includes(num));
      setParcelasSelecionadas(novasSelecionadas);
    }
  };

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-body">
              <div className="card-header">
                <strong>Repactuação de Parcela</strong>
              </div>

              <div className="row" style={{ marginTop: '10px' }}>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Contrato <span style={{ color: 'red' }}>*</span></label>
                    <input 
                      type="text" 
                      className="form-control form-control-sm brds" 
                      style={{ color: 'Black', backgroundColor: 'White' }}
                      value={contrato} 
                      onChange={(e) => { setContrato(e.target.value); limpaDados(); }} 
                      onKeyDown={(e) => e.key === 'Enter' && listarContrato()}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <button className="input-group-btn"
                    style={{
                      backgroundColor: 'white', width: '30%', marginLeft: '10px', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '54px', minWidth: '50px'
                    }} 
                    onClick={listarContrato}
                  >
                    <FontAwesomeIcon icon={faSearch} style={{ color: 'black' }} />
                  </button>
                </div>
              </div>

              {exibeDetalhe ? (
                <>
                  <div className="row" style={{ marginTop: '10px' }}>
                    <div className="col-md-3">
                      <div className="form-group">
                        <span style={{ textAlign: 'left' }}>Nome Cliente</span>
                        <input disabled type="text" value={nomeCliente} className="form-control form-control-sm brds" style={{ color: 'Black', backgroundColor: 'LightGrey' }} />
                      </div>
                    </div>
                  </div>

                  <div className="row" style={{ marginTop: '10px' }}>
                    <div className="col-md-8">
                      <div className="col-md-4">
                        <label>Faixa de Parcela <span style={{ color: 'red' }}>*</span></label>
                        <table style={{ width: '100%' }}>
                          <tbody>
                            <tr>
                              <td style={{ width: "150px" }}>
                                <InputMask required maskChar="" mask="999" type="text" className="form-control" value={faixaParcela1} onChange={(e) => setFaixaParcela1((e.target.value).replace(/_/g, ""))} />
                              </td>
                              <td style={{ textAlign: 'center', width: "30px" }}> a </td>
                              <td style={{ width: "150px" }}>
                                <InputMask required maskChar="" mask="999" type="text" className="form-control" value={faixaParcela2} onChange={(e) => setFaixaParcela2((e.target.value).replace(/_/g, ""))} />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="row" style={{ marginTop: '15px' }}>
                    <div className="col-md-3">
                      <button className="input-group-btn" id="App-button-cor-principal"
                        style={{
                          width: '40%', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                        }} 
                        onClick={calcularFaixa}
                      >
                        <FontAwesomeIcon icon={faCalculator} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                        <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Calcular</strong>
                      </button>
                    </div>
                  </div>

                  {parcelasList!.length > 0 ? (
                    <div className="table-responsive">
                      <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                        <table style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                          <tbody>
                           <tr className="GridHeaderStyle" style={{ backgroundColor: 'white', color: 'black' }}>
                              <th style={{ textAlign: 'center', width: '8%', verticalAlign: 'middle' }} scope="col" id="App-header-table">
                                <input
                                  type="checkbox"
                                  checked={todasParcelasSelecionadas}
                                  onChange={(e) => toggleSelecionarTodas(e.target.checked)}
                                  disabled={parcelasElegiveisNaTela.length === 0}
                                  aria-label="Selecionar todas as parcelas elegíveis"
                                  style={{ cursor: parcelasElegiveisNaTela.length === 0 ? 'not-allowed' : 'pointer', transform: 'scale(1.2)' }}
                                /> <span style={{ marginLeft: '5px' }}>Tudo</span>
                              </th>
                              <th style={{ textAlign: 'center', verticalAlign: 'middle' }} scope="col" id="App-header-table">NÚMERO</th>
                              <th style={{ textAlign: 'center', verticalAlign: 'middle' }} scope="col" id="App-header-table">VENCIMENTO</th>
                              <th style={{ textAlign: 'center', verticalAlign: 'middle' }} scope="col" id="App-header-table">STATUS</th>
                              <th style={{ textAlign: 'center', verticalAlign: 'middle' }} scope="col" id="App-header-table">VALOR PARCELA</th>
                              <th style={{ textAlign: 'center', verticalAlign: 'middle' }} scope="col" id="App-header-table">SALDO DEVEDOR</th>
                            </tr>
                            {parcelasList!.map((item) => {
                             const tipoMov = (item.tipoMovimentacao || '').toUpperCase();
                              const jaFoiMovimentada = Boolean(item.dtMovimentacao) || (item.valorPago != null && item.valorPago > 0);
                              const jaFoiRepactuadaOuBaixada = tipoMov.includes('REPACTU') || tipoMov.includes('BAIXA') || tipoMov.includes('ESTORNO');

                              const isBloqueada = jaFoiMovimentada || jaFoiRepactuadaOuBaixada;
                              const selecionada = parcelasSelecionadas.includes(item.numeroParcela!);

                              return (
                                <tr className="GridRowStyle" key={item.id || item.numeroParcela} style={{ opacity: isBloqueada ? 0.5 : 1 }}>
                                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                    <input
                                      type="checkbox"
                                      disabled={isBloqueada}
                                      checked={selecionada}
                                      onChange={() => toggleSelecionarParcela(item.numeroParcela!)}
                                      style={{ cursor: isBloqueada ? 'not-allowed' : 'pointer', transform: 'scale(1.2)' }}
                                    />
                                  </td>
                                  <td style={{ textAlign: 'center' }}>{item.numeroParcela}</td>
                                  <td style={{ textAlign: 'center' }}>{item.dtVencimento ? format(parseISO(item.dtVencimento.toString()), 'dd/MM/yyyy') : ''}</td>
                                  <td style={{ textAlign: 'center' }}>{item.tipoMovimentacao ? item.tipoMovimentacao.replace(/_/g, ' ') : 'EM ABERTO'}</td>
                                  <td style={{ textAlign: 'center' }}>R$ {formatarValorMoeda(item.valorParcela)}</td>
                                  <td style={{ textAlign: 'center' }}>R$ {formatarValorMoeda(item.saldo || item.valorParcela)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="row" style={{ marginTop: '15px', height: '50px', maxWidth: '70%' }}>
                        <div className="col-md-4">
                          <button className="input-group-btn" id="App-button-cor-principal"
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                            }} 
                            onClick={executarRepactuacaoLote}
                            disabled={parcelasSelecionadas.length === 0}
                          >
                            <FontAwesomeIcon icon={faSave} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                            <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Confirmar Repactuação</strong>
                          </button>
                        </div>
                        <div className="col-md-4">
                          <button className="input-group-btn"
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', backgroundColor: 'red', borderColor: 'red',
                              justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                            }} 
                            onClick={() => window.history.back()}
                          >
                            <FontAwesomeIcon icon={faClose} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                            <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Cancelar</strong>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RepactuacaoParcelaComponent;